import { Component, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FieldsetModule } from 'primeng/fieldset';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TextareaModule } from 'primeng/textarea';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

import { CumplimientoRepository } from 'src/app/domain/service/cumplimiento/cumplimiento-repository';
import { CatalogoRepository } from 'src/app/domain/service/catalogo/catalogo-repository';
import { CatalogoOption } from 'src/app/domain/model/catalogo/catalogo';
import {
  RiesgoPotencialDetalle,
  RiesgoSeguimiento,
  GuardarRiesgoSeguimientoRequest,
  ESTADO_RIESGO,
  ESTADOS_SOLO_REAPERTURA
} from 'src/app/domain/model/cumplimiento/cumplimiento';
import { sanitizeTextarea } from 'src/app/common/input-utils';
import { environment } from 'src/environments/environment';
import { openExternalWindow } from 'src/app/common/window-utils';

/**
 * Container: Seguimiento de Alerta Temprana (Riesgo Potencial)
 *
 * Legacy: SeguimientoRiesgoPotViewImpl.java + SeguimientoRiesgoPotViewImpl.ui.xml
 * Backend:
 *  - GET  /api/servicios/orientacionesProgramadas/riesgos-potenciales/{idRiesgo}
 *  - PUT  /api/servicios/orientacionesProgramadas/riesgos-potenciales/{idRiesgo}
 *  - GET  /api/servicios/orientacionesProgramadas/riesgos-potenciales/{idRiesgo}/seguimientos
 *  - POST /api/servicios/orientacionesProgramadas/riesgos-potenciales/{idRiesgo}/seguimientos
 *
 * Regla de habilitación (reset(dto) del legado):
 * - Si cdestado está en {RPCR, RPE3, RPCA, RPCD} -> solo se permite Reapertura.
 * - Si cdestado es RPRP o el riesgo se solucionó en sitio -> no se muestra el histórico.
 */
const TIPOS_ARCHIVO_EVIDENCIA_PERMITIDOS = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];
const MAX_EVIDENCIA_FILE_SIZE = 10 * 1024 * 1024;

/** Legacy: WidgetsNamesConstants.msgEstadoCerradoRiesgo */
const MSG_ESTADO_CERRADO = 'El estado de la alerta temprana no permite registrar seguimientos.';
/** Legacy: WidgetsNamesConstants.msgErrorFechaSegRiesgoPot */
const MSG_FECHA_SEGUIMIENTO = 'La fecha de seguimiento debe estar entre hoy y los próximos 15 días.';

/**
 * Descarga del documento en P8.
 * Legacy: submitForm a ConsumeServiceNoReturnAction con sr_accion=ID_SRV_CONSULTA_DOC_PO.
 */
const URL_DOCUMENTO_P8 = `${environment.backend.baseUrl}/servicios/documentos/p8`;
const VENTANA_DOCUMENTO = 'toolbar=no,resizable=yes,menubar=no,location=no,status=yes,scrollbars=yes';

@Component({
  selector: 'app-seguimiento-riesgo-potencial',
  standalone: true,
  styleUrls: ['../../shared/styles/grid-table.styles.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    FieldsetModule,
    RadioButtonModule,
    TextareaModule,
    DropdownModule,
    InputTextModule,
    DatePickerModule,
    TableModule,
    TooltipModule,
    ToastModule
  ],
  providers: [MessageService],
  template: `
    <div class="p-3 sm:p-4 lg:p-6 max-w-5xl mx-auto overflow-x-hidden">
      <p-toast></p-toast>

      @if (!modoModal()) {
        <div class="text-sm text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-2">
          <span>Cumplimientos</span>
          <span class="text-gray-400 dark:text-gray-500">»</span>
          <a class="cursor-pointer hover:underline" (click)="volver()">Alertas Tempranas</a>
          <span class="text-gray-400 dark:text-gray-500">»</span>
          <span class="font-medium text-gray-800 dark:text-gray-100">Seguimiento</span>
        </div>
      }

      @if (cargandoDetalle()) {
        <p class="text-gray-500">Cargando alerta temprana...</p>
      } @else if (!detalle()) {
        <div
          class="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-300"
        >
          No se encontró la alerta temprana solicitada.
        </div>
      } @else {
        <!-- Botonera — legacy: saEditRP | saRegresar -->
        <div class="mb-4 flex flex-wrap gap-2">
          <button
            pButton
            type="button"
            label="Modificar Detalle"
            icon="pi pi-pencil"
            class="p-button-outlined"
            (click)="abrirEdicion()"
          ></button>
          <button
            pButton
            type="button"
            label="Regresar"
            icon="pi pi-arrow-left"
            class="p-button-text"
            (click)="volver()"
          ></button>
        </div>

        <p-fieldset legend="Detalle Riesgo" [toggleable]="true" styleClass="mb-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div><span class="text-gray-500">Consecutivo:</span> {{ detalle()!.nmconsecutivo }}</div>
            <div>
              <span class="text-gray-500">Estado:</span>
              {{ detalle()!.dsestado ?? detalle()!.cdestado }}
            </div>
            <div class="sm:col-span-2">
              <span class="text-gray-500">Descripción del detalle de la situación actual:</span>
              {{ detalle()!.dssituacion }}
            </div>
            <div class="sm:col-span-2">
              <span class="text-gray-500">Consecuencia:</span> {{ detalle()!.dsconsecuencia }}
            </div>
            <div><span class="text-gray-500">Riesgo que le aplica:</span> {{ detalle()!.dsriesgo }}</div>
            <div>
              <span class="text-gray-500">Número de personas expuestas:</span>
              {{ detalle()!.nmpersonasexp }}
            </div>
            <div>
              <span class="text-gray-500">¿Como consultor también me encuentro expuesto a este riesgo?:</span>
              {{ detalle()!.snasesorExpuesto === 'S' ? 'SI' : 'NO' }}
            </div>
            <div>
              <span class="text-gray-500">¿El riesgo identificado se solucionó en el sitio?:</span>
              {{ detalle()!.snsolucionSitio === 'S' ? 'SI' : 'NO' }}
            </div>
            <div class="sm:col-span-2">
              <span class="text-gray-500">Ubicación donde se presenta la situación (Sede / Área / Proceso):</span>
              {{ detalle()!.dssede }} / {{ detalle()!.dsarea }} / {{ detalle()!.dsproceso }}
            </div>
            <div class="sm:col-span-2">
              <span class="text-gray-500">Detalle de la ubicación:</span> {{ detalle()!.dsdetubicacion }}
            </div>
            <div class="sm:col-span-2">
              <span class="text-gray-500">Recomendación:</span> {{ detalle()!.dsrecomendacion }}
            </div>
            <div><span class="text-gray-500">Tipo de control:</span> {{ detalle()!.dscontrol }}</div>
            <div>
              <span class="text-gray-500">Fecha seguimiento:</span>
              {{ detalle()!.feseguimiento | date: 'dd/MM/yyyy' }}
            </div>
            <!-- Legacy: trEvidenciaRP + saVerEvidencia, visibles sólo si hay documento -->
            @if (detalle()!.cdevidenciap8) {
              <div>
                <span class="text-gray-500">Evidencia:</span>
                <a
                  class="cursor-pointer text-sura-blue-500 hover:underline"
                  (click)="verDocumento(detalle()!.cdevidenciap8!)"
                  >Visualizar</a
                >
              </div>
            }
          </div>
        </p-fieldset>

        @if (!estadoSoloReportado()) {
          <p-fieldset legend="Histórico de Seguimiento" [toggleable]="true" styleClass="mb-4">
            <!-- Mobile: cards (skill sura-ui 11.6.1 — patrón híbrido validado) -->
            <div class="md:hidden space-y-3">
              @for (seg of historial(); track seg.nmconsecutivo ?? $index) {
                <article
                  class="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800"
                >
                  <div class="flex items-start justify-between gap-2 mb-2">
                    <span
                      class="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate"
                      [title]="tituloTabla(seg.dsestado ?? seg.cdestado)"
                    >
                      {{ textoTabla(seg.dsestado ?? seg.cdestado) }}
                    </span>
                    <span class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {{ seg.feingresa | date: 'dd/MM/yyyy' }}
                    </span>
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-3">
                    <div>
                      <span class="text-gray-500 dark:text-gray-400">Aplica Recomendación:</span>
                      {{ seg.snaplicaRecomend === 'S' ? 'SI' : 'NO' }}
                    </div>
                    <div>
                      <span class="text-gray-500 dark:text-gray-400">Acción Suficiente:</span>
                      {{ seg.snaccionSufic === 'S' ? 'SI' : 'NO' }}
                    </div>
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      pButton
                      type="button"
                      icon="pi pi-search"
                      label="Detalle"
                      class="p-button-outlined p-button-sm w-full min-h-[44px]"
                      (click)="verDetalleSeguimiento(seg)"
                    ></button>
                    @if (seg.cddocumento) {
                      <button
                        pButton
                        type="button"
                        icon="pi pi-file"
                        label="Evidencia"
                        class="p-button-outlined p-button-sm w-full min-h-[44px]"
                        (click)="verDocumento(seg.cddocumento)"
                      ></button>
                    }
                  </div>
                </article>
              } @empty {
                <div class="text-center text-gray-500 dark:text-gray-400 py-3">Sin seguimientos registrados</div>
              }
            </div>

            <!-- Desktop/tablet: tabla completa, dentro de modal (skill sura-ui 11.6.3) -->
            <div class="hidden md:block">
              <p-table
                [value]="historial()"
                [loading]="cargandoHistorial()"
                [paginator]="historial().length > 10"
                [rows]="10"
                [rowsPerPageOptions]="[10, 25, 50]"
                [showCurrentPageReport]="true"
                currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
                [scrollable]="true"
                [responsiveLayout]="'scroll'"
                scrollHeight="320px"
                [resizableColumns]="true"
                columnResizeMode="expand"
                [tableStyle]="{ 'min-width': '100%' }"
                styleClass="tabla-riesgo-historico-seguimiento p-datatable-sm p-datatable-gridlines p-datatable-striped"
              >
                <ng-template pTemplate="header">
                  <tr>
                    <th pResizableColumn class="text-center" style="width: 7rem">
                      <span class="grid-header-text" title="Fecha">Fecha</span>
                    </th>
                    <th pResizableColumn style="width: 10rem">
                      <span class="grid-header-text" title="Estado">Estado</span>
                    </th>
                    <th pResizableColumn class="text-center" style="width: 9rem">
                      <span class="grid-header-text" title="Aplica Recomendación">Aplica Recomendación</span>
                    </th>
                    <th pResizableColumn class="text-center" style="width: 8rem">
                      <span class="grid-header-text" title="Acción Suficiente">Acción Suficiente</span>
                    </th>
                    <th pResizableColumn class="text-center" style="width: 6.5rem">
                      <span class="grid-header-text" title="Detalle">Detalle</span>
                    </th>
                    <th pResizableColumn class="text-center" style="width: 6.5rem">
                      <span class="grid-header-text" title="Evidencia">Evidencia</span>
                    </th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-seg>
                  <tr>
                    <td class="text-center whitespace-nowrap">{{ seg.feingresa | date: 'dd/MM/yyyy' }}</td>
                    <td>
                      <span
                        class="grid-cell-text"
                        [attr.title]="tituloTabla(seg.dsestado ?? seg.cdestado)"
                        >{{ textoTabla(seg.dsestado ?? seg.cdestado) }}</span
                      >
                    </td>
                    <td class="text-center">{{ seg.snaplicaRecomend === 'S' ? 'SI' : 'NO' }}</td>
                    <td class="text-center">{{ seg.snaccionSufic === 'S' ? 'SI' : 'NO' }}</td>
                    <td class="text-center">
                      <button
                        pButton
                        type="button"
                        icon="pi pi-search"
                        class="p-button-text p-button-sm min-h-[44px]"
                        label="Visualizar"
                        pTooltip="Ver detalle del seguimiento"
                        tooltipPosition="top"
                        (click)="verDetalleSeguimiento(seg)"
                      ></button>
                    </td>
                    <td class="text-center">
                      @if (seg.cddocumento) {
                        <button
                          pButton
                          type="button"
                          icon="pi pi-file"
                          class="p-button-text p-button-sm min-h-[44px]"
                          label="Visualizar"
                          pTooltip="Ver documento de evidencia"
                          tooltipPosition="top"
                          (click)="verDocumento(seg.cddocumento)"
                        ></button>
                      }
                    </td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                  <tr>
                    <td colspan="6" class="text-center text-gray-500 dark:text-gray-400 py-3">
                      Sin seguimientos registrados
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            </div>
          </p-fieldset>
        }

        @if (soloReapertura()) {
          <div
            class="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800 dark:border-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 mb-4"
          >
            {{ MSG_ESTADO_CERRADO }}
          </div>
          @if (!confirmandoReapertura()) {
            <button
              pButton
              type="button"
              label="Reapertura"
              icon="pi pi-refresh"
              class="p-button-warning"
              (click)="confirmandoReapertura.set(true)"
            ></button>
          } @else {
            <div class="flex flex-wrap items-center gap-3">
              <span>¿Confirma la reapertura de esta alerta temprana?</span>
              <button pButton type="button" label="Confirmar" [loading]="guardando()" (click)="reabrir()"></button>
              <button
                pButton
                type="button"
                label="Cancelar"
                class="p-button-outlined"
                [disabled]="guardando()"
                (click)="confirmandoReapertura.set(false)"
              ></button>
            </div>
          }
        } @else {
          <p-fieldset legend="Registro de seguimiento" [toggleable]="true">
            <div class="flex flex-col gap-4">
              <div>
                <label class="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                  ¿El riesgo es potencial mortal inminente? <span class="text-red-600">*</span>
                </label>
                <div class="flex items-center gap-6">
                  <div class="flex items-center gap-2">
                    <p-radioButton
                      name="riesgoInminente"
                      [value]="'S'"
                      [(ngModel)]="riesgoInminente"
                      inputId="riesgoInminenteSi"
                    ></p-radioButton>
                    <label for="riesgoInminenteSi" class="cursor-pointer">Sí</label>
                  </div>
                  <div class="flex items-center gap-2">
                    <p-radioButton
                      name="riesgoInminente"
                      [value]="'N'"
                      [(ngModel)]="riesgoInminente"
                      inputId="riesgoInminenteNo"
                    ></p-radioButton>
                    <label for="riesgoInminenteNo" class="cursor-pointer">No</label>
                  </div>
                </div>
              </div>

              @if (riesgoInminente === 'S') {
                <div>
                  <label class="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Cerrado por afiliación <span class="text-red-600">*</span>
                  </label>
                  <div class="flex items-center gap-6">
                    <div class="flex items-center gap-2">
                      <p-radioButton
                        name="cerradoAfilia"
                        [value]="'S'"
                        [(ngModel)]="cerradoAfilia"
                        inputId="cerradoAfiliaSi"
                      ></p-radioButton>
                      <label for="cerradoAfiliaSi" class="cursor-pointer">Sí</label>
                    </div>
                    <div class="flex items-center gap-2">
                      <p-radioButton
                        name="cerradoAfilia"
                        [value]="'N'"
                        [(ngModel)]="cerradoAfilia"
                        inputId="cerradoAfiliaNo"
                      ></p-radioButton>
                      <label for="cerradoAfiliaNo" class="cursor-pointer">No</label>
                    </div>
                  </div>
                </div>
              }

              @if (riesgoInminente === 'S' && cerradoAfilia === 'N') {
                <div>
                  <label class="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                    ¿Se aplicó la recomendación sugerida? <span class="text-red-600">*</span>
                  </label>
                  <div class="flex items-center gap-6">
                    <div class="flex items-center gap-2">
                      <p-radioButton
                        name="aplicaRecomend"
                        [value]="'S'"
                        [(ngModel)]="aplicaRecomend"
                        inputId="aplicaRecomendSi"
                      ></p-radioButton>
                      <label for="aplicaRecomendSi" class="cursor-pointer">Sí</label>
                    </div>
                    <div class="flex items-center gap-2">
                      <p-radioButton
                        name="aplicaRecomend"
                        [value]="'N'"
                        [(ngModel)]="aplicaRecomend"
                        inputId="aplicaRecomendNo"
                      ></p-radioButton>
                      <label for="aplicaRecomendNo" class="cursor-pointer">No</label>
                    </div>
                  </div>
                </div>

                <div>
                  <label for="observacion" class="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Observación del seguimiento <span class="text-red-600">*</span>
                  </label>
                  <textarea
                    pTextarea
                    id="observacion"
                    rows="3"
                    class="w-full"
                    maxlength="1000"
                    [(ngModel)]="observacion"
                    (input)="observacion = sanitizeTextarea(observacion)"
                  ></textarea>
                </div>

                @if (aplicaRecomend === 'S') {
                  <div>
                    <label class="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                      ¿Esta acción es suficiente para eliminar/controlar el riesgo?
                      <span class="text-red-600">*</span>
                    </label>
                    <div class="flex items-center gap-6">
                      <div class="flex items-center gap-2">
                        <p-radioButton
                          name="accionSuficiente"
                          [value]="'S'"
                          [(ngModel)]="accionSuficiente"
                          inputId="accionSuficienteSi"
                        ></p-radioButton>
                        <label for="accionSuficienteSi" class="cursor-pointer">Sí</label>
                      </div>
                      <div class="flex items-center gap-2">
                        <p-radioButton
                          name="accionSuficiente"
                          [value]="'N'"
                          [(ngModel)]="accionSuficiente"
                          inputId="accionSuficienteNo"
                        ></p-radioButton>
                        <label for="accionSuficienteNo" class="cursor-pointer">No</label>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label for="evidencia" class="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                      Anexar evidencias <span class="text-red-600">*</span>
                    </label>
                    <input
                      #fileInput
                      id="evidencia"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      (change)="onArchivoSeleccionado($event)"
                      class="text-sm"
                    />
                    @if (archivoSeleccionado()) {
                      <div class="mt-1 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <i class="pi pi-file"></i>
                        <span>{{ archivoNombre() }}</span>
                        <button
                          pButton
                          type="button"
                          icon="pi pi-times"
                          class="p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                          pTooltip="Quitar archivo"
                          (click)="limpiarArchivo(fileInput)"
                        ></button>
                      </div>
                    }
                    @if (errorArchivo()) {
                      <p class="text-xs text-red-600 dark:text-red-400 mt-1">{{ errorArchivo() }}</p>
                    }
                  </div>
                }

                @if (aplicaRecomend === 'N' || accionSuficiente === 'N') {
                  <div>
                    <label for="recomendacion" class="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                      Recomendación <span class="text-red-600">*</span>
                    </label>
                    <textarea
                      pTextarea
                      id="recomendacion"
                      rows="3"
                      class="w-full"
                      maxlength="1000"
                      [(ngModel)]="recomendacion"
                      (input)="recomendacion = sanitizeTextarea(recomendacion)"
                    ></textarea>
                  </div>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label for="control" class="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Tipo de control <span class="text-red-600">*</span>
                      </label>
                      <p-dropdown
                        inputId="control"
                        [options]="controlesRiesgo()"
                        [(ngModel)]="controlSeleccionado"
                        optionLabel="descripcion"
                        optionValue="codigo"
                        placeholder="Seleccione un control"
                        [filter]="true"
                        filterBy="descripcion"
                        [showClear]="true"
                        appendTo="body"
                        class="w-full"
                        styleClass="w-full"
                      />
                    </div>
                    <div>
                      <label for="feseguimiento" class="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Fecha seguimiento (hoy a hoy+15) <span class="text-red-600">*</span>
                      </label>
                      <p-datepicker
                        inputId="feseguimiento"
                        [(ngModel)]="feseguimiento"
                        dateFormat="dd/mm/yy"
                        [showIcon]="true"
                        appendTo="body"
                      />
                    </div>
                  </div>
                }
              }

              @if (mensajeValidacion()) {
                <div
                  class="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-300"
                >
                  {{ mensajeValidacion() }}
                </div>
              }

              <div class="flex justify-end">
                <button
                  pButton
                  type="button"
                  label="Guardar"
                  icon="pi pi-check"
                  [loading]="guardando()"
                  [disabled]="!formularioListo()"
                  (click)="guardar()"
                ></button>
              </div>
            </div>
          </p-fieldset>
        }
      }

      <!-- Legacy: panelPopupDetalleSeg -->
      <p-dialog
        header="Detalle del seguimiento"
        [(visible)]="detalleSeguimientoVisible"
        [modal]="true"
        [style]="{ width: '92vw', maxWidth: '40rem' }"
        [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
        [draggable]="false"
      >
        @if (seguimientoSeleccionado(); as seg) {
          <div class="flex flex-col gap-3 text-sm">
            <div>
              <p class="text-gray-500 dark:text-gray-400">Observación del seguimiento</p>
              <p class="whitespace-pre-line">{{ seg.dsobservacion }}</p>
            </div>
            <!-- Legacy: htmlTableSegRec sólo si el seguimiento registró recomendación -->
            @if (seg.dsrecomendacion) {
              <hr class="border-sura-gray-200 dark:border-sura-gray-800" />
              <div>
                <p class="text-gray-500 dark:text-gray-400">Recomendación</p>
                <p class="whitespace-pre-line">{{ seg.dsrecomendacion }}</p>
              </div>
              <div>
                <span class="text-gray-500 dark:text-gray-400">Tipo de control:</span> {{ seg.dscontrol }}
              </div>
              <div>
                <span class="text-gray-500 dark:text-gray-400">Fecha seguimiento:</span>
                {{ seg.feseguimiento | date: 'dd/MM/yyyy' }}
              </div>
            }
          </div>
        }
        <ng-template pTemplate="footer">
          <div class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full">
            <button
              pButton
              type="button"
              label="Cerrar"
              icon="pi pi-times"
              class="p-button-outlined min-h-[44px]"
              (click)="detalleSeguimientoVisible = false"
            ></button>
          </div>
        </ng-template>
      </p-dialog>

      <!-- Legacy: panelPopupEditRiesgo («Modificar Detalle») -->
      <p-dialog
        header="Modificar Detalle"
        [(visible)]="edicionVisible"
        [modal]="true"
        [style]="{ width: '44rem' }"
        [draggable]="false"
      >
        <div class="flex flex-col gap-4 text-sm">
          <div>
            <label for="edRiesgo" class="block text-gray-600 dark:text-gray-400 mb-1">
              Riesgo que le aplica <span class="text-red-600">*</span>
            </label>
            <p-dropdown
              inputId="edRiesgo"
              [options]="agentesRiesgo()"
              [(ngModel)]="edicionRiesgo"
              optionLabel="descripcion"
              optionValue="codigo"
              placeholder="Seleccione el riesgo"
              [filter]="true"
              filterBy="descripcion"
              appendTo="body"
              styleClass="w-full"
            />
          </div>

          @if (esAlertaSinOrden()) {
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label for="edAsesor" class="block text-gray-600 dark:text-gray-400 mb-1">Asesor</label>
                <input
                  pInputText
                  id="edAsesor"
                  type="number"
                  class="w-full"
                  [(ngModel)]="edicionAsesor"
                />
              </div>
              <div>
                <label for="edProfesional" class="block text-gray-600 dark:text-gray-400 mb-1">
                  Profesional de prevención
                </label>
                <input
                  pInputText
                  id="edProfesional"
                  type="number"
                  class="w-full"
                  [(ngModel)]="edicionProfesional"
                />
              </div>
            </div>
          }

          <div>
            <label for="edSituacion" class="block text-gray-600 dark:text-gray-400 mb-1">
              Descripción del detalle de la situación actual <span class="text-red-600">*</span>
            </label>
            <textarea
              pTextarea
              id="edSituacion"
              rows="4"
              class="w-full"
              maxlength="1000"
              [(ngModel)]="edicionSituacion"
              (input)="edicionSituacion = sanitizeTextarea(edicionSituacion)"
            ></textarea>
          </div>

          <div>
            <label for="edRecomendacion" class="block text-gray-600 dark:text-gray-400 mb-1">
              Recomendación <span class="text-red-600">*</span>
            </label>
            <textarea
              pTextarea
              id="edRecomendacion"
              rows="4"
              class="w-full"
              maxlength="1000"
              [(ngModel)]="edicionRecomendacion"
              (input)="edicionRecomendacion = sanitizeTextarea(edicionRecomendacion)"
            ></textarea>
          </div>

          @if (mensajeEdicion()) {
            <div
              class="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-300"
            >
              {{ mensajeEdicion() }}
            </div>
          }
        </div>
        <ng-template pTemplate="footer">
          <button
            pButton
            type="button"
            label="Cancelar"
            class="p-button-text"
            [disabled]="guardandoEdicion()"
            (click)="edicionVisible = false"
          ></button>
          <button
            pButton
            type="button"
            label="Aceptar"
            [loading]="guardandoEdicion()"
            (click)="guardarEdicion()"
          ></button>
        </ng-template>
      </p-dialog>
    </div>
  `
})
export class SeguimientoRiesgoPotencialComponent implements OnInit {
  private readonly repository = inject(CumplimientoRepository);
  private readonly catalogoRepository = inject(CatalogoRepository);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);

  readonly MSG_ESTADO_CERRADO = MSG_ESTADO_CERRADO;

  readonly controlesRiesgo = signal<CatalogoOption[]>([]);
  readonly agentesRiesgo = signal<CatalogoOption[]>([]);

  readonly sanitizeTextarea = sanitizeTextarea;

  readonly idRiesgoInput = input<number | null>(null);
  readonly modoModal = input<boolean>(false);
  readonly cerrar = output<void>();

  idRiesgo = 0;

  readonly detalle = signal<RiesgoPotencialDetalle | null>(null);
  readonly historial = signal<RiesgoSeguimiento[]>([]);
  readonly cargandoDetalle = signal(true);
  readonly cargandoHistorial = signal(false);
  readonly guardando = signal(false);
  readonly confirmandoReapertura = signal(false);
  readonly mensajeValidacion = signal<string | null>(null);

  readonly estadoSoloReportado = computed(
    () => this.detalle()?.cdestado === ESTADO_RIESGO.REPORTADO || this.detalle()?.snsolucionSitio === 'S'
  );
  readonly soloReapertura = computed(() => {
    const estado = this.detalle()?.cdestado;
    return !!estado && ESTADOS_SOLO_REAPERTURA.includes(estado);
  });
  /** Legacy: en riesgos con cumplimiento, asesor y profesional vienen de la orden. */
  readonly esAlertaSinOrden = computed(() => this.detalle()?.nmconsecutivoCumpl == null);

  // === Formulario de seguimiento (equivalente a getDto() del legado) ===
  riesgoInminente: 'S' | 'N' | null = null;
  /** Legacy: initListBox() preselecciona «No». */
  cerradoAfilia: 'S' | 'N' | null = 'N';
  aplicaRecomend: 'S' | 'N' | null = null;
  accionSuficiente: 'S' | 'N' | null = null;
  observacion = '';
  recomendacion = '';
  controlSeleccionado: string | null = null;
  feseguimiento: Date | null = null;

  // === Detalle del histórico (legacy: panelPopupDetalleSeg) ===
  detalleSeguimientoVisible = false;
  readonly seguimientoSeleccionado = signal<RiesgoSeguimiento | null>(null);

  // === Modificar detalle (legacy: panelPopupEditRiesgo) ===
  edicionVisible = false;
  edicionSituacion = '';
  edicionRecomendacion = '';
  edicionRiesgo: string | null = null;
  edicionAsesor: number | null = null;
  edicionProfesional: number | null = null;
  readonly guardandoEdicion = signal(false);
  readonly mensajeEdicion = signal<string | null>(null);

  // === Evidencia (legacy: uploaderEvidencia) ===
  readonly archivoSeleccionado = signal(false);
  readonly archivoNombre = signal('');
  readonly errorArchivo = signal<string | null>(null);
  private archivoContenidoBase64 = '';
  private archivoContentType = '';

  readonly formularioListo = computed(() => {
    if (this.riesgoInminente === null) return false;
    if (this.riesgoInminente === 'N') return true;
    if (this.cerradoAfilia === null) return false;
    if (this.cerradoAfilia === 'S') return true;
    if (this.aplicaRecomend === null || !this.observacion.trim()) return false;
    // Legacy: con recomendación aplicada, «acción suficiente» y la evidencia son obligatorias.
    if (this.aplicaRecomend === 'S' && (this.accionSuficiente === null || !this.archivoSeleccionado())) {
      return false;
    }
    return true;
  });

  async ngOnInit(): Promise<void> {
    this.idRiesgo = this.idRiesgoInput() ?? Number(this.route.snapshot.paramMap.get('id'));
    if (!this.idRiesgo || this.idRiesgo <= 0) {
      this.cargandoDetalle.set(false);
      return;
    }
    await this.cargarCatalogos();
    await this.cargarDetalle();
    await this.cargarHistorial();
  }

  private async cargarCatalogos(): Promise<void> {
    try {
      const [controles, agentes] = await Promise.all([
        this.catalogoRepository.getControlesRiesgo(),
        this.catalogoRepository.getAgentesRiesgo()
      ]);
      this.controlesRiesgo.set(controles);
      this.agentesRiesgo.set(agentes);
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Catálogos',
        detail: 'No fue posible cargar los catálogos de riesgo y control.'
      });
    }
  }

  private async cargarDetalle(): Promise<void> {
    this.cargandoDetalle.set(true);
    try {
      const detalle = await this.repository.obtenerRiesgoPotencial(this.idRiesgo);
      this.detalle.set(detalle);
    } catch {
      this.detalle.set(null);
    } finally {
      this.cargandoDetalle.set(false);
    }
  }

  private async cargarHistorial(): Promise<void> {
    this.cargandoHistorial.set(true);
    try {
      const historial = await this.repository.listarSeguimientosRiesgo(this.idRiesgo);
      this.historial.set(historial ?? []);
    } catch {
      this.historial.set([]);
    } finally {
      this.cargandoHistorial.set(false);
    }
  }

  /** Legacy: saRegresar -> place.getPlaceOrigen(), o la consulta de alertas por defecto. */
  volver(): void {
    if (this.modoModal()) {
      this.cerrar.emit();
      return;
    }
    const origen = this.route.snapshot.queryParamMap.get('origen');
    this.router.navigateByUrl(origen ?? '/riesgos-potenciales');
  }

  /** Legacy: saVerEvidencia / columna «Evidencia» -> consulta del documento en P8. */
  verDocumento(idDocumento: string): void {
    openExternalWindow(
      `${URL_DOCUMENTO_P8}/${encodeURIComponent(idDocumento)}`,
      VENTANA_DOCUMENTO
    );
  }

  /** Legacy: visualizarDetalleSeg(). */
  verDetalleSeguimiento(seguimiento: RiesgoSeguimiento): void {
    this.seguimientoSeleccionado.set(seguimiento);
    this.detalleSeguimientoVisible = true;
  }

  /** Estándar GIROP de tabla (skill sura-ui 11.4.1): texto visible con fallback '-'. */
  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  /** Estándar GIROP de tabla (skill sura-ui 11.4.1): valor completo para el atributo title. */
  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }

  // ==================== Modificar Detalle ====================

  /** Legacy: onClickEditRP() — precarga el diálogo con los datos actuales. */
  abrirEdicion(): void {
    const detalle = this.detalle();
    if (!detalle) {
      return;
    }
    this.edicionSituacion = detalle.dssituacion ?? '';
    this.edicionRecomendacion = detalle.dsrecomendacion ?? '';
    this.edicionRiesgo = detalle.nmconsecRiesgo != null ? String(detalle.nmconsecRiesgo) : null;
    this.edicionAsesor = detalle.nmconsecutivoAsesor ?? null;
    this.edicionProfesional = detalle.nmconsecProfesional ?? null;
    this.mensajeEdicion.set(null);
    this.edicionVisible = true;
  }

  /** Legacy: onClickAceptar() -> comando guardarRiesgoPot. */
  async guardarEdicion(): Promise<void> {
    this.mensajeEdicion.set(null);

    if (!this.edicionRiesgo) {
      this.mensajeEdicion.set('El riesgo que le aplica es obligatorio.');
      return;
    }
    if (!this.edicionSituacion.trim()) {
      this.mensajeEdicion.set('La descripción del detalle de la situación actual es obligatoria.');
      return;
    }
    if (!this.edicionRecomendacion.trim()) {
      this.mensajeEdicion.set('La recomendación es obligatoria.');
      return;
    }

    this.guardandoEdicion.set(true);
    try {
      const actualizado = await this.repository.actualizarRiesgoPotencial(this.idRiesgo, {
        dssituacion: this.edicionSituacion.trim(),
        dsrecomendacion: this.edicionRecomendacion.trim(),
        nmconsecRiesgo: Number(this.edicionRiesgo),
        nmconsecutivoAsesor: this.esAlertaSinOrden() ? this.edicionAsesor ?? undefined : undefined,
        nmconsecProfesional: this.esAlertaSinOrden() ? this.edicionProfesional ?? undefined : undefined
      });
      this.detalle.set(actualizado);
      this.edicionVisible = false;
      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'La alerta temprana se actualizó correctamente.'
      });
    } catch (error) {
      this.mensajeEdicion.set(this.extraerMensajeError(error, 'No fue posible actualizar la alerta temprana.'));
    } finally {
      this.guardandoEdicion.set(false);
    }
  }

  // ==================== Registro de seguimiento ====================

  /** Legacy: validarFechaSegRiesgoPotencial() — ventana [hoy, hoy+15]. */
  private fechaSeguimientoEnVentana(): boolean {
    if (!this.feseguimiento) return false;
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const limite = new Date(hoy);
    limite.setDate(limite.getDate() + 15);
    const fecha = new Date(this.feseguimiento);
    fecha.setHours(0, 0, 0, 0);
    return fecha >= hoy && fecha <= limite;
  }

  private construirPayload(): GuardarRiesgoSeguimientoRequest | null {
    this.mensajeValidacion.set(null);

    // Legacy: onClickGrabar -> getDto()
    if (this.riesgoInminente === 'N') {
      return {
        dsobservacion: 'CERRADO NO INMINENTE',
        snaccionSufic: 'S',
        sncerradoApl: 'S',
        snaplicaRecomend: 'N'
      };
    }

    if (this.cerradoAfilia === 'S') {
      return {
        dsobservacion: 'CERRADO POR DESAFILIACION',
        snaccionSufic: 'S',
        sncerradoApl: 'N',
        sncerradoAfilia: 'S',
        snaplicaRecomend: 'N'
      };
    }

    if (!this.observacion.trim()) {
      this.mensajeValidacion.set('La observación del seguimiento es obligatoria.');
      return null;
    }

    // Legacy: rbgAccionSuficiente y el adjunto son obligatorios cuando se aplicó la
    // recomendación sugerida (setChangeHandlers + validateUploader).
    if (this.aplicaRecomend === 'S') {
      if (this.accionSuficiente === null) {
        this.mensajeValidacion.set(
          'Debe indicar si esta acción es suficiente para eliminar/controlar el riesgo.'
        );
        return null;
      }
      if (!this.archivoSeleccionado()) {
        this.mensajeValidacion.set('Debe anexar la evidencia del seguimiento.');
        return null;
      }
    }

    const requiereRecomendacion = this.aplicaRecomend === 'N' || this.accionSuficiente === 'N';
    if (requiereRecomendacion) {
      if (!this.recomendacion.trim()) {
        this.mensajeValidacion.set('Cuando la acción no es suficiente o no aplica recomendación, la recomendación es obligatoria.');
        return null;
      }
      if (!this.controlSeleccionado) {
        this.mensajeValidacion.set('Cuando la acción no es suficiente o no aplica recomendación, el tipo de control es obligatorio.');
        return null;
      }
      if (!this.feseguimiento) {
        this.mensajeValidacion.set('Cuando la acción no es suficiente o no aplica recomendación, la fecha de seguimiento es obligatoria.');
        return null;
      }
      if (!this.fechaSeguimientoEnVentana()) {
        this.mensajeValidacion.set(MSG_FECHA_SEGUIMIENTO);
        return null;
      }
    }

    return {
      dsobservacion: this.observacion.trim(),
      dsrecomendacion: requiereRecomendacion ? this.recomendacion.trim() : undefined,
      feseguimiento: requiereRecomendacion && this.feseguimiento
        ? this.toIsoDate(this.feseguimiento)
        : undefined,
      snaccionSufic: this.accionSuficiente ?? undefined,
      sncerradoApl: 'N',
      sncerradoAfilia: 'N',
      snaplicaRecomend: this.aplicaRecomend ?? 'N',
      nmconsecControl: requiereRecomendacion && this.controlSeleccionado
        ? Number(this.controlSeleccionado)
        : undefined
    };
  }

  private toIsoDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /** Legacy: uploaderEvidencia — carga y validación del adjunto. */
  async onArchivoSeleccionado(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    this.errorArchivo.set(null);

    if (!TIPOS_ARCHIVO_EVIDENCIA_PERMITIDOS.includes(file.type)) {
      this.errorArchivo.set('Tipo de archivo no permitido. Use PDF, JPG, PNG, DOC o DOCX.');
      this.limpiarArchivo(input);
      return;
    }
    if (file.size > MAX_EVIDENCIA_FILE_SIZE) {
      this.errorArchivo.set('El archivo excede el tamaño máximo de 10 MB.');
      this.limpiarArchivo(input);
      return;
    }

    try {
      this.archivoContenidoBase64 = await this.fileToBase64(file);
      this.archivoContentType = file.type;
      this.archivoNombre.set(file.name);
      this.archivoSeleccionado.set(true);
    } catch {
      this.errorArchivo.set('Error al leer el archivo.');
      this.limpiarArchivo(input);
    }
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.onerror = (error) => reject(error);
    });
  }

  limpiarArchivo(input?: HTMLInputElement): void {
    this.archivoSeleccionado.set(false);
    this.archivoNombre.set('');
    this.archivoContenidoBase64 = '';
    this.archivoContentType = '';
    if (input) {
      input.value = '';
    }
  }

  async guardar(): Promise<void> {
    const payload = this.construirPayload();
    if (!payload) {
      return;
    }

    if (this.aplicaRecomend === 'S' && this.archivoSeleccionado()) {
      this.guardando.set(true);
      try {
        const evidencia = await this.repository.cargarEvidenciaSeguimientoRiesgo(
          this.idRiesgo,
          this.archivoContenidoBase64,
          this.archivoNombre(),
          this.archivoContentType
        );
        payload.cddocumento = evidencia.idDocumento;
      } catch (error) {
        this.guardando.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.extraerMensajeError(error, 'No fue posible cargar la evidencia.')
        });
        return;
      }
      this.guardando.set(false);
    }

    await this.enviarSeguimiento(payload, 'Seguimiento registrado correctamente.');
  }

  /** Legacy: onClickReapertura(). */
  async reabrir(): Promise<void> {
    const payload: GuardarRiesgoSeguimientoRequest = {
      dsobservacion: 'REAPERTURA',
      snaccionSufic: 'S',
      sncerradoApl: 'N',
      snaplicaRecomend: 'N',
      cdestado: ESTADO_RIESGO.REABIERTO
    };
    await this.enviarSeguimiento(payload, 'Alerta temprana reabierta correctamente.');
  }

  private async enviarSeguimiento(payload: GuardarRiesgoSeguimientoRequest, mensajeExito: string): Promise<void> {
    this.guardando.set(true);
    try {
      await this.repository.guardarSeguimientoRiesgo(this.idRiesgo, payload);
      this.messageService.add({ severity: 'success', summary: 'Éxito', detail: mensajeExito });
      this.resetFormulario();
      await this.cargarDetalle();
      await this.cargarHistorial();
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.extraerMensajeError(error, 'No fue posible registrar el seguimiento.')
      });
    } finally {
      this.guardando.set(false);
      this.confirmandoReapertura.set(false);
    }
  }

  private extraerMensajeError(error: unknown, porDefecto: string): string {
    const err = error as { error?: { message?: string } };
    return err?.error?.message ?? porDefecto;
  }

  private resetFormulario(): void {
    this.riesgoInminente = null;
    this.cerradoAfilia = 'N';
    this.aplicaRecomend = null;
    this.accionSuficiente = null;
    this.observacion = '';
    this.recomendacion = '';
    this.controlSeleccionado = null;
    this.feseguimiento = null;
    this.mensajeValidacion.set(null);
    this.limpiarArchivo();
  }
}
